/**
 * Country code formatting helpers for the admin UI.
 *
 * Organizations store their country as an uppercased ISO-3166 alpha-2 code
 * (see backend `OrganizationService` — `country.Trim().ToUpperInvariant()`),
 * so we render it as a flag emoji plus a localized country name (e.g. "🇺🇦 Ukraine").
 * Legal addresses, however, may carry a free-form value, so anything that is
 * not a clean two-letter code is returned unchanged.
 */

// Offset from an ASCII uppercase letter to its Regional Indicator Symbol
// (U+1F1E6 'A' − U+0041 'A' = 127397).
const REGIONAL_INDICATOR_OFFSET = 127397;

/** Converts an alpha-2 code into its flag emoji (e.g. "UA" → "🇺🇦"). */
export function countryFlag(code: string): string {
  return code
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .replace(/./g, (c) => String.fromCodePoint(REGIONAL_INDICATOR_OFFSET + c.charCodeAt(0)));
}

/** Localized country name for an alpha-2 code, falling back to the raw code. */
export function countryName(code: string, locale = 'en'): string {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

/**
 * Formats a country value for display: "🇺🇦 Ukraine" for an alpha-2 code, the
 * value as-is for anything else, or `null` when empty.
 */
export function formatCountry(code?: string | null, locale = 'en'): string | null {
  if (!code) return null;
  const trimmed = code.trim();
  if (!trimmed) return null;
  if (!/^[A-Za-z]{2}$/.test(trimmed)) return trimmed;
  return `${countryFlag(trimmed)} ${countryName(trimmed, locale)}`;
}
